<!--
  Unsupported grid widget: shows attempted plain value (or i18n when missing), copy raw from data, open drawer on value click.
-->
<script setup lang="ts">
import { computed, onBeforeUnmount, ref, withDefaults } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { resolvePassportFieldPaletteSource } from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import { shouldShowPassportRuntimeValueTooltip } from '../model/passportRuntimeFieldDisplay'
import { runtimePassportFieldLabelFromMeta } from '../model/passportRuntimeFieldLabel'
import { useTranslate } from '@/shared/lib'
import { VIcons } from '@/shared/ui'
import type { RuntimePlaceholderItem } from '../model/runtimeLayoutAdapter'

const props = withDefaults(
  defineProps<{
    item: RuntimePlaceholderItem
    catalogTypeId: number
    allFields: ObjectInventoryField[]
    /** Plain text the cell would have shown if supported. */
    displayText: string
    /** Raw clipboard payload when API exposes a leaf id in `fieldValues`. */
    rawCopy: string
    /** Leaf field id for drawer scroll/focus; null if not in merged metadata. */
    focusFieldId: string | null
    /**
     * When set to `true`/`false`, show read-only `VCheckbox` (same affordance as supported boolean fields).
     * `null` — show `displayText` only.
     */
    readonlyBooleanSwitch?: boolean | null
    /** When false, value click does not open the all-fields drawer. */
    clickable?: boolean
    /** Подсветка после failed save (обязательное пустое поле). */
    showMandatoryError?: boolean
  }>(),
  {
    clickable: true,
    showMandatoryError: false,
  },
)

const emit = defineEmits<{ openDrawer: [fieldId: string | null] }>()

const { trs } = useTranslate()
const toastOpen = ref(false)
const copied = ref(false)
let copyResetTimer: ReturnType<typeof setTimeout> | undefined

onBeforeUnmount(() => {
  if (copyResetTimer) clearTimeout(copyResetTimer)
})

const fieldMeta = computed(() => {
  const layoutId = String(props.item.layoutItem.id)
  const byLayout = props.allFields.find((f) => String(f.id) === layoutId)
  if (byLayout) return byLayout
  if (props.focusFieldId) {
    const byFocus = props.allFields.find((f) => String(f.id) === String(props.focusFieldId))
    if (byFocus) return byFocus
  }
  const ownerMatch = layoutId.match(/^ref:\[\d+:([A-Za-z0-9]+):\d+]/)
  if (ownerMatch?.[1]) {
    return props.allFields.find((f) => String(f.id) === ownerMatch[1])
  }
  const prefixed = layoutId.match(/^(?:parent|element):([^:]+):.+$/)
  if (prefixed?.[1]) {
    const byPrefixed = props.allFields.find((f) => String(f.id) === prefixed[1])
    if (byPrefixed) return byPrefixed
  }
  const layoutName = props.item.layoutItem.name?.trim()
  if (layoutName) {
    return props.allFields.find((f) => f.name?.trim() === layoutName)
  }
  return undefined
})

const cellTitle = computed(() =>
  runtimePassportFieldLabelFromMeta(fieldMeta.value, props.item.layoutItem.name),
)

const palette = computed(() =>
  resolvePassportFieldPaletteSource(props.item.layoutItem, props.catalogTypeId, fieldMeta.value),
)

const cardModifierClass = computed(() => ({
  'passport-runtime-v2__unsupported-field-card--own': palette.value === 'own',
  'passport-runtime-v2__unsupported-field-card--parent': palette.value === 'parent',
  'passport-runtime-v2__unsupported-field-card--list': palette.value === 'list',
  'passport-runtime-v2__unsupported-field-card--element': palette.value === 'element',
}))

const showReadonlyBooleanSwitch = computed(
  () => props.readonlyBooleanSwitch === true || props.readonlyBooleanSwitch === false,
)

/**
 * Parent dictionary chrome: green «on» switch. Use persisted `palette_source` — `resolvePassportFieldPaletteSource`
 * can fall back to `own` for `ref:` cells when metadata/`inventory_type` heuristics disagree.
 */
const parentPaletteBoolean = computed(
  () => palette.value === 'parent' || String(props.item.layoutItem.palette_source ?? '') === 'parent',
)

const booleanSwitchColor = computed(() => (parentPaletteBoolean.value ? 'success' : 'primary'))

const showValueTooltip = computed(() =>
  shouldShowPassportRuntimeValueTooltip({
    fieldType: fieldMeta.value?.type,
    layoutPaletteSource: props.item.layoutItem.palette_source,
    displayText: props.displayText,
  }),
)

function onValueClick() {
  if (!props.clickable) return
  emit('openDrawer', props.focusFieldId)
}

async function onCopy(e: MouseEvent) {
  e.stopPropagation()
  const raw = props.rawCopy ?? ''
  if (raw === '') {
    toastOpen.value = true
    return
  }
  try {
    await navigator.clipboard.writeText(raw)
    copied.value = true
    if (copyResetTimer) clearTimeout(copyResetTimer)
    copyResetTimer = setTimeout(() => {
      copied.value = false
      copyResetTimer = undefined
    }, 1600)
  } catch {
    copied.value = false
  }
}
</script>

<template>
  <div
    class="passport-runtime-v2__unsupported-field-card"
    :class="[
      cardModifierClass,
      { 'passport-runtime-v2__unsupported-field-card--mandatory-error': showMandatoryError },
    ]"
  >
    <div class="passport-runtime-v2__unsupported-field-card__name" :title="cellTitle">
      {{ cellTitle }}
    </div>
    <div class="passport-runtime-v2__unsupported-field-card__row">
      <div class="passport-runtime-v2__unsupported-field-card__value-wrap">
        <VTooltip location="top" :eager="false" :disabled="!showValueTooltip">
          <template #activator="{ props: tipProps }">
            <div
              v-bind="tipProps"
              class="passport-runtime-v2__unsupported-field-card__value"
              :class="{
                'passport-runtime-v2__unsupported-field-card__value--clickable': clickable,
                'passport-runtime-v2__unsupported-field-card__value--boolean-switch': showReadonlyBooleanSwitch,
                'passport-runtime-v2__unsupported-field-card__value--error': showMandatoryError,
              }"
              role="button"
              tabindex="-1"
              @click="onValueClick"
            >
              <VCheckbox
                v-if="showReadonlyBooleanSwitch"
                class="passport-runtime-v2__unsupported-field-card__switch passport-runtime-v2__field-input"
                :class="{
                  'passport-runtime-v2__unsupported-field-card__switch--parent-palette': parentPaletteBoolean,
                }"
                :model-value="Boolean(readonlyBooleanSwitch)"
                :color="booleanSwitchColor"
                density="compact"
                hide-details
                readonly
                @click.stop
              />
              <span
                v-else
                class="passport-runtime-v2__unsupported-field-card__value-text passport-runtime-v2__ellipsis"
              >
                {{ displayText }}
              </span>
            </div>
          </template>
          <span>{{ displayText }}</span>
        </VTooltip>
      </div>
      <button
        type="button"
        class="passport-runtime-v2__unsupported-field-card__copy"
        :aria-label="trs('itf.copy')"
        @click.stop="onCopy"
      >
        <VIcons
          v-if="!copied"
          type="content_copy"
          class="passport-runtime-v2__unsupported-field-card__icon"
        />
        <VIcons v-else type="check" class="passport-runtime-v2__unsupported-field-card__icon" />
      </button>
    </div>
    <div
      v-if="showMandatoryError"
      class="passport-runtime-v2__unsupported-field-card__error"
    >
      {{ trs('validate.required') }}
    </div>
    <VSnackbar v-model="toastOpen" location="bottom" :timeout="2200" color="surface" variant="tonal">
      {{ trs('itf.passport_runtime_copy_empty_toast') }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.passport-runtime-v2__unsupported-field-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.passport-runtime-v2__unsupported-field-card--mandatory-error
  .passport-runtime-v2__unsupported-field-card__name {
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__unsupported-field-card__value--error {
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 4px;
}

.passport-runtime-v2__unsupported-field-card__error {
  margin-top: 4px;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}

/** Dictionary / palette accent for copy icon (card root had no `color`, so `inherit` resolved to black). */
.passport-runtime-v2__unsupported-field-card--own {
  --prv2-unsupported-copy: rgb(var(--v-theme-primary));
}

.passport-runtime-v2__unsupported-field-card--parent {
  --prv2-unsupported-copy: #00838f;
}

.passport-runtime-v2__unsupported-field-card--list {
  --prv2-unsupported-copy: rgb(var(--v-theme-primary));
}

.passport-runtime-v2__unsupported-field-card--element {
  --prv2-unsupported-copy: #3f51b5;
}

.passport-runtime-v2__unsupported-field-card__row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.passport-runtime-v2__unsupported-field-card__value-wrap {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  z-index: 0;
}

.passport-runtime-v2__unsupported-field-card__copy {
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  touch-action: manipulation;
  /* `VIcons` is a child SFC — scoped rules miss its root span without :deep(); global `.v-icon` stays primary. */
  color: var(--prv2-unsupported-copy, rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity))) !important;
}

.passport-runtime-v2__unsupported-field-card__copy :deep(.passport-runtime-v2__unsupported-field-card__icon.material-symbols-rounded.v-icon),
.passport-runtime-v2__unsupported-field-card__copy :deep(span.material-symbols-rounded.v-icon) {
  font-size: 18px;
  width: 18px;
  height: 18px;
  color: var(--prv2-unsupported-copy, rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity))) !important;
  opacity: 1 !important;
}

.passport-runtime-v2__unsupported-field-card__value {
  flex: 1 1 auto;
  min-width: 0;
}

.passport-runtime-v2__unsupported-field-card__value--clickable {
  cursor: pointer;
}

.passport-runtime-v2__unsupported-field-card__value--boolean-switch {
  display: flex;
  align-items: center;
  min-height: 0;
}

.passport-runtime-v2__unsupported-field-card__switch {
  flex: 0 0 auto;
  pointer-events: none;
  /** Default VInput heights (40–56px) are for touch targets; match single-line text rows in grid cards. */
  --v-input-control-height: 28px;
  --v-input-padding-top: 0px;
  --v-selection-control-size: 28px;
}



.passport-runtime-v2__ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
